import { IAnalyticsServiceConfiguration } from '../../../services/analytics'
import { AnalyticsMetaKeys as key } from '../../../services/analytics/formatters/meta-keys'
import { Utils } from '../../../utils/common'
import { DOMUtils } from '../../../utils/dom'
import { KlangooUtils } from '../../../utils/klangoo'
import { IMain } from '../../conf/page'

const NLP_MAX_LIST_SIZE = 5
const NLP_ENTITY_SCORE_THRESHOLD = .8
const UTM_SRC = 'utm_source'
const HUNDRED = 100
const TEST_ENV = 'test'
const PROD_ENV = 'prod'

export interface IGlobalDataFormatter {
    lookup(attributeName: string, event?: Event): string| string[]
}

export class GlobalDataFormatter implements IGlobalDataFormatter {

    public constructor(private conf: IAnalyticsServiceConfiguration) {
        return
    }

    /**
     * get the value of a global attribute given its attribute name
     * @param: string
     * @returns: string
     */

    public lookup(attributeName: string, event?: Event): string | string[] {
        let attributeValue: any = ''
        switch (attributeName) {
            case key.USER_STATUS:
                if (this.conf.profile.adLight) {
                    attributeValue = 'subscribed'
                } else if (this.conf.profile.email) {
                    attributeValue = 'registered'
                } else {
                    attributeValue = 'anonymous'
                }
                break
            case key.AD_LIGHT:
                attributeValue = String(this.conf.profile.adLight)
                break
            case key.BRAND:
                attributeValue = this.conf.brand
                break
            case key.DIVISION:
                attributeValue = this.conf.division
                break
            case key.DOMAIN:
                attributeValue = Utils.getDomain()
                break
            case key.TYPE:
                attributeValue = this.conf.page.type
                break
            case key.METERED_CONTENT:
                attributeValue = String(!this.conf.page.isPaywallWhitelisted)
                break
            case key.ARTICLE_ID:
                attributeValue = this.conf.page.articleId
                break
            case key.IS_ORIGINAL:
                if (typeof this.conf.page.isOrigin === 'boolean') {
                    attributeValue = String(this.conf.page.isOrigin)
                }
                break
            case key.WRD_CNT:
                attributeValue = this.conf.page.wc
                break
            case key.CHAR_CNT:
                attributeValue = this.conf.page.charCount
                break
            case key.SPONSOR_NAME:
                if (this.conf.page.sponsor) {
                    attributeValue = this.conf.page.sponsor
                }
                break
            case key.IS_WIRE:
                attributeValue = (this.conf.page.writers &&
                    this.conf.page.writers.wire &&
                    this.conf.page.writers.wire.length > 0) ||
                    this.conf.page.wireId ? String(true): String(false)
                break
            case key.ARTICLE_AUTHORS:
                if (this.conf.page.writers && this.conf.page.writers.main) {
                    attributeValue = this.conf.page.writers.main.map((_: IMain) =>
                        typeof _ === 'object' ? _.name : _).join('|')
                }
                break
            case key.ARTICLE_AUTHOR_LIST:
                if (this.conf.page.writers && this.conf.page.writers.main) {
                    attributeValue = this.conf.page.writers.main.map((_: IMain) =>
                        typeof _ === 'object' ? _.name : _)
                }
                break
            case key.WIRE_AUTHORS:
                if (this.conf.page.writers && this.conf.page.writers.wire) {
                    attributeValue = this.conf.page.writers.wire.join('|')
                }
                break
            case key.ARTICLE_TAGS:
                if (this.conf.page.tags) {
                    attributeValue = this.conf.page.tags.join(', ')
                }
                break
            case key.ARTICLE_TAG_LIST:
                if (this.conf.page.tags) {
                    attributeValue = this.conf.page.tags
                }
                break
            case key.TITLE:
                attributeValue = this.conf.page.getTitle()
                break
            case key.HEADLINE:
                attributeValue = this.conf.page.getHeadline()
                break
            case key.MAIN_CATEGORY:
                const categories = this.conf.page.getCategories()
                if (categories.length) {
                    attributeValue = categories[categories.length - 1]
                }
                break
            case key.PUB_TIMESTAMP:
                if (this.conf.page.date && this.conf.page.date.pub_timestamp) {
                    attributeValue = String(this.conf.page.date.pub_timestamp)
                }
                break
            case key.NLP_ENTITY:
                if(this.conf.page.klangoo && this.conf.page.klangoo.entities) {
                    attributeValue = KlangooUtils.stringifyFilteredEntities(
                        this.conf.page.klangoo.entities, NLP_MAX_LIST_SIZE).join('|')
                }
                break
            case key.NLP_ENTITY_LIST:
                if(this.conf.page.klangoo && this.conf.page.klangoo.entities) {
                    attributeValue = KlangooUtils.stringifyFilteredEntities(
                        this.conf.page.klangoo.entities, NLP_MAX_LIST_SIZE, NLP_ENTITY_SCORE_THRESHOLD)
                }
                break
            case key.NLP_TOPIC:
                if(this.conf.page.klangoo && this.conf.page.klangoo.keyTopics) {
                    attributeValue = KlangooUtils.stringifyFilteredKeytopics(
                        this.conf.page.klangoo.keyTopics, NLP_MAX_LIST_SIZE)
                }
                break
            case key.NLP_CATEGORY:
                if(this.conf.page.klangoo && this.conf.page.klangoo.categories) {
                    attributeValue =  KlangooUtils.stringifyFilteredCategories(
                        this.conf.page.klangoo.categories).join(',')
                }
                break
            case key.NLP_CATEGORY_LIST:
                if(this.conf.page.klangoo && this.conf.page.klangoo.categories) {
                    attributeValue =  KlangooUtils.stringifyFilteredCategories(
                        this.conf.page.klangoo.categories)
                }
                break
            case key.PAGE_URL:
                attributeValue = Utils.currentPageUrl()
                break
            case key.REF_DETAILS:
                const params = Utils.urlParams(document.location)
                if (params.has(UTM_SRC)) {
                    attributeValue = params.get(UTM_SRC)
                }
                break
            case key.URL_PARAMS:
                if(this.conf.page.captureUrlParams) {
                    const urlSearchParams = new URLSearchParams(window.location.search)
                    attributeValue = {}
                    this.conf.page.captureUrlParams.forEach((p: string) => {
                        attributeValue[p] = urlSearchParams.get(p)
                    })
                }
                break
            case key.PROJECT_TYPE:
                if(this.conf.page.projectType !== undefined) {
                    attributeValue = String(this.conf.page.projectType)
                }
                break
            case key.PLATFORM:
                attributeValue = this.conf.platform
                break
            case key.PLATFORM_VERSION:
                attributeValue = this.conf.platformVersion
                break
            case key.FEM_VERSION:
                attributeValue = this.conf.femVersion
                break
            case key.VIEW_TYPE:
                attributeValue = 'HTML'
                break
            case key.CLICK_SOURCE_VERTICAL_POSITION_PERCENTAGE:
                if(document.body.clientHeight && (event as MouseEvent).pageY) {
                    attributeValue = String(Math.round((event as MouseEvent).pageY/document.body.clientHeight*HUNDRED))
                }
                break
            case key.CLICK_SOURCE_VERTICAL_POSITION_PIXELS:
                if((event as MouseEvent).pageY) {
                    attributeValue = String((event as MouseEvent).pageY)
                }
                break
            case key.DEVICE_TYPE:
                attributeValue = DOMUtils.deviceType()
                break
            case key.AD_BLOCKER:
                // use Piano snippet for adblocker detection added as part of Cheetah
                attributeValue = (document.cookie.split(';').find((c) =>
                    c.trim().startsWith(`__adblocker=`)) || '').trim().split('=').pop() || 'false'
                break
            case key.PAGEVIEW_PRCNT_DEPRECATED:
            case key.PAGEVIEW_PRCNT:
                attributeValue = String(this.conf.scrollData[key.PAGEVIEW_PRCNT])
                break
            case key.STORYVIEW_PRCNT_DEPRECATED:
            case key.STORYVIEW_PRCNT:
                attributeValue = String(this.conf.scrollData[key.STORYVIEW_PRCNT])
                break
            case key.ENV:
                attributeValue =  (this.conf.isDev) ? TEST_ENV : PROD_ENV
                break
            case key.CAR_MAKE:
                if(this.conf.page.drivingTaxonomy && this.conf.page.drivingTaxonomy.carMake) {
                    attributeValue = String(this.conf.page.drivingTaxonomy.carMake)
                }
                break
            case key.CAR_MODEL:
                if(this.conf.page.drivingTaxonomy && this.conf.page.drivingTaxonomy.carModel) {
                    attributeValue = String(this.conf.page.drivingTaxonomy.carModel)
                }
                break
            case key.CAR_YEAR:
                if(this.conf.page.drivingTaxonomy && this.conf.page.drivingTaxonomy.modelYears) {
                    attributeValue = String(this.conf.page.drivingTaxonomy.modelYears)
                }
                break
            case key.CAR_BODYSTYLE:
                if(this.conf.page.drivingTaxonomy && this.conf.page.drivingTaxonomy.bodyType) {
                    attributeValue = String(this.conf.page.drivingTaxonomy.bodyType)
                }
                break
            case key.CAR_CLASSIFICATION:
                if(this.conf.page.drivingTaxonomy && this.conf.page.drivingTaxonomy.vehicleType) {
                    attributeValue = String(this.conf.page.drivingTaxonomy.vehicleType)
                }
                break
            case key.BROWSER:
                attributeValue = DOMUtils.browserType()
                break
            case key.BROWSER_LG:
                attributeValue = DOMUtils.browserLanguage()
                break
            // Custom Flags
            case key.GA_NONINTERACTION:
                (attributeValue as unknown as boolean) = true
                break
            case key.GA_PAGE_TITLE:
                attributeValue = this.conf.page.getTitle()
                break
            case key.GA_PAGE_PATH:
                attributeValue = Utils.getPath()
                break
            case key.SECONDARY_CATEGORIES:
                if(this.conf.page.secondary_categories) {
                    attributeValue = this.conf.page.secondary_categories.join(',')
                }
                break
            case key.SECONDARY_CATEGORIES_LIST:
                if(this.conf.page.secondary_categories) {
                    attributeValue = this.conf.page.secondary_categories
                }
                break
        }
        return attributeValue
    }
}
